<div class="page-header">
  <div>
    <p class="eyebrow">Management</p>
    <h1>Rules & BRDs</h1>
  </div>
</div>

<div class="toolbar">
  <p-select
    [options]="repositories()"
    [ngModel]="selectedRepo()"
    (ngModelChange)="selectRepo($event)"
    optionLabel="repoName"
    placeholder="Select a repository"
    [showClear]="true"
    [filter]="true"
    filterBy="repoName,projectName,organization"
    styleClass="w-80">
    <ng-template let-repo pTemplate="selectedItem">
      <span class="repo-option">
        <strong>{{ repo.repoName }}</strong>
        <span class="repo-option-sub">{{ repo.projectName }}</span>
      </span>
    </ng-template>
    <ng-template let-repo pTemplate="item">
      <div class="repo-option repo-option-row">
        <strong>{{ repo.repoName }}</strong>
        <span class="repo-option-sub">{{ repo.projectName }} · {{ repo.organization }}</span>
      </div>
    </ng-template>
  </p-select>

  @if (selectedRepo()) {
    <div class="tab-group">
      <button
        class="tab-btn"
        [class.active]="activeTab() === 'rules'"
        (click)="switchTab('rules')">
        Rules
      </button>
      <button
        class="tab-btn"
        [class.active]="activeTab() === 'brds'"
        (click)="switchTab('brds')">
        BRDs
      </button>
    </div>

    <p-button
      [label]="activeTab() === 'rules' ? '+ Add Rule' : '+ Add BRD'"
      (onClick)="activeTab() === 'rules' ? openAddRule() : openAddBrd()"
      size="small">
    </p-button>

    @if (activeTab() === 'rules' && otherProjects().length > 0) {
      <p-button
        label="Copy from another project"
        (onClick)="openCopyPanel()"
        size="small"
        severity="secondary"
        [outlined]="true">
      </p-button>
    }
  }
</div>

@if (!selectedRepo()) {
  <div class="empty-state">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
    </svg>
    <p>Select a repository to manage its review rules and business requirements.</p>
  </div>
} @else {

  @if (showRuleForm()) {
    <div class="form-panel">
      <h3>{{ ruleForm().id ? 'Edit Rule' : 'New Rule' }}</h3>
      <div class="form-grid">
        <div class="field field--full">
          <label>Scope</label>
          <p-select
            [options]="scopeOptions()"
            [ngModel]="currentRuleScope()"
            (ngModelChange)="onRuleScopeChange($event)"
            optionLabel="label"
            placeholder="Select scope"
            styleClass="w-full">
          </p-select>
        </div>

        <div class="field">
          <label>Category</label>
          <p-select
            [options]="categories()"
            [ngModel]="selectedCategory()"
            (ngModelChange)="selectedCategory.set($event); patchRule('categoryId', $event?.id ?? 0)"
            optionLabel="name"
            placeholder="Select category"
            styleClass="w-full">
          </p-select>
        </div>

        <div class="field">
          <label>Priority</label>
          <p-select
            [options]="priorityOptions"
            [ngModel]="selectedPriority()"
            (ngModelChange)="selectedPriority.set($event); patchRule('priority', $event?.value ?? 2)"
            optionLabel="label"
            placeholder="Select priority"
            styleClass="w-full">
          </p-select>
        </div>

        <div class="field field--full">
          <label>Title</label>
          <input
            pInputText
            type="text"
            class="w-full"
            [ngModel]="ruleForm().title"
            (ngModelChange)="patchRule('title', $event)"
            placeholder="Short label for this rule" />
        </div>

        <div class="field field--full">
          <label>Rule Text</label>
          <textarea
            pTextarea
            rows="4"
            class="w-full"
            [ngModel]="ruleForm().ruleText"
            (ngModelChange)="patchRule('ruleText', $event)"
            placeholder="Full rule description…"
            [autoResize]="false">
          </textarea>
        </div>

        <div class="field field--checkbox">
          <p-checkbox
            [ngModel]="ruleForm().isActive"
            (ngModelChange)="patchRule('isActive', $event)"
            [binary]="true"
            label="Active rule (included in AI reviews)">
          </p-checkbox>
        </div>

        @if (!ruleForm().id && otherProjects().length > 0) {
          <div class="field field--full">
            <label>Also apply to (project-wide)</label>
            <p-multiselect
              [options]="otherProjects()"
              [ngModel]="additionalProjectIds()"
              (ngModelChange)="additionalProjectIds.set($event)"
              optionLabel="projectName"
              optionValue="projectId"
              [filter]="true"
              filterBy="projectName,organization"
              placeholder="Select additional projects (optional)"
              styleClass="w-full"
              display="chip">
            </p-multiselect>
            <small class="field-hint">When picked, this rule will also be created project-wide in those projects.</small>
          </div>
        }
      </div>

      @if (saveError()) {
        <p-message severity="error" [text]="saveError()!" styleClass="mb-3 w-full"></p-message>
      }

      <div class="form-actions">
        <p-button
          [label]="saving() ? 'Saving…' : 'Save Rule'"
          [loading]="saving()"
          (onClick)="saveRule()">
        </p-button>
        <p-button
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (onClick)="cancelRuleForm()">
        </p-button>
      </div>
    </div>
  }

  @if (showBrdForm()) {
    <div class="form-panel">
      <div class="form-panel-head">
        <h3>{{ brdForm().id ? 'Edit BRD' : 'New BRD' }}</h3>
        <label class="pdf-upload-btn" [class.loading]="pdfLoading()">
          <input
            type="file"
            accept=".pdf,application/pdf"
            (change)="onPdfUpload($event)"
            [disabled]="pdfLoading()"
            hidden />
          @if (pdfLoading()) {
            <span>{{ pdfProgressLabel() }}</span>
          } @else {
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            Import from PDF
          }
        </label>
      </div>

      @if (pdfError()) {
        <p-message severity="warn" [text]="pdfError()!" styleClass="mb-3 w-full"></p-message>
      }

      <p class="pdf-hint">
        <span>ⓘ</span>
        PDF import uses OCR and can make mistakes — always review the output in <strong>Preview</strong> and touch up before saving.
      </p>


      <div class="tab-group form-tabs">
        <button class="tab-btn" [class.active]="brdFormTab() === 'edit'" (click)="switchBrdFormTab('edit')" type="button">Edit</button>
        <button class="tab-btn" [class.active]="brdFormTab() === 'preview'" (click)="switchBrdFormTab('preview')" type="button">Preview</button>
      </div>

      @if (brdFormTab() === 'edit') {
        <div class="form-grid">
          <div class="field field--full">
            <label>Scope</label>
            <p-select
              [options]="scopeOptions()"
              [ngModel]="currentBrdScope()"
              (ngModelChange)="onBrdScopeChange($event)"
              optionLabel="label"
              placeholder="Select scope"
              styleClass="w-full">
            </p-select>
          </div>

          <div class="field">
            <label>Title</label>
            <input
              pInputText
              type="text"
              class="w-full"
              [ngModel]="brdForm().title"
              (ngModelChange)="patchBrd('title', $event)"
              placeholder="e.g. Shareholder Export v1.2" />
          </div>

          <div class="field">
            <label>Version</label>
            <input
              pInputText
              type="text"
              class="w-full"
              [ngModel]="brdForm().version"
              (ngModelChange)="patchBrd('version', $event)"
              placeholder="e.g. 1.2" />
          </div>

          <div class="field field--full">
            <label>Content (Markdown)</label>
            <textarea
              pTextarea
              rows="14"
              class="w-full brd-content-editor"
              [ngModel]="brdForm().content"
              (ngModelChange)="patchBrd('content', $event)"
              placeholder="Paste BRD content here…"
              [autoResize]="false"
              dir="auto">
            </textarea>
          </div>
        </div>
      } @else {
        <div class="brd-preview-pane">
          @if (brdForm().title) {
            <h2 class="preview-title" dir="auto">{{ brdForm().title }}</h2>
          }
          @if (brdForm().version) {
            <p class="preview-version">Version {{ brdForm().version }}</p>
          }
          <app-markdown-preview [source]="brdForm().content"></app-markdown-preview>
          @if (!brdForm().content.trim()) {
            <p class="preview-empty">Nothing to preview yet. Switch to Edit or import a PDF.</p>
          }
        </div>
      }

      @if (saveError()) {
        <p-message severity="error" [text]="saveError()!" styleClass="mb-3 w-full"></p-message>
      }

      <div class="form-actions">
        <p-button
          [label]="saving() ? 'Saving…' : 'Save BRD'"
          [loading]="saving()"
          (onClick)="saveBrd()">
        </p-button>
        <p-button
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (onClick)="cancelBrdForm()">
        </p-button>
      </div>
    </div>
  }

  @if (showCopyPanel()) {
    <div class="form-panel">
      <h3>Copy rules from another project</h3>
      <div class="form-grid">
        <div class="field">
          <label>Source project</label>
          <p-select
            [options]="otherProjects()"
            [ngModel]="copySourceProjectId()"
            (ngModelChange)="onCopySourceChange($event)"
            optionLabel="projectName"
            optionValue="projectId"
            [filter]="true"
            filterBy="projectName,organization"
            placeholder="Pick a project to copy from"
            styleClass="w-full">
          </p-select>
        </div>

        <div class="field">
          <label>Target scope (in {{ selectedRepo()?.projectName }})</label>
          <p-select
            [options]="scopeOptions()"
            [ngModel]="currentCopyTargetScope()"
            (ngModelChange)="onCopyTargetScopeChange($event)"
            optionLabel="label"
            placeholder="Select scope"
            styleClass="w-full">
          </p-select>
        </div>
      </div>

      @if (copyError()) {
        <p-message severity="error" [text]="copyError()!" styleClass="mb-3 w-full"></p-message>
      }

      @if (copyLoading()) {
        <div class="empty-state">Loading rules…</div>
      } @else if (copySourceProjectId()) {
        @if (copySourceRules().length === 0) {
          <div class="empty-state"><p>That project has no rules to copy.</p></div>
        } @else {
          <div class="copy-rules-list">
            <label class="copy-rules-toggle-all">
              <p-checkbox
                [ngModel]="copyAllChecked"
                (ngModelChange)="toggleCopyAll()"
                [binary]="true">
              </p-checkbox>
              <span><strong>{{ copySelectedRuleIds().size }}</strong> of {{ copySourceRules().length }} selected</span>
            </label>
            <ul class="copy-rules-items">
              @for (rule of copySourceRules(); track rule.id) {
                <li>
                  <p-checkbox
                    [ngModel]="isCopyRuleSelected(rule.id)"
                    (ngModelChange)="toggleCopyRule(rule.id)"
                    [binary]="true">
                  </p-checkbox>
                  <div class="copy-rule-meta">
                    <div class="copy-rule-title">
                      <span class="scope-pill" [class.scope-repo]="rule.repositoryId">
                        {{ rule.repositoryName ?? 'Project-wide' }}
                      </span>
                      <strong>{{ rule.title }}</strong>
                      <p-tag [value]="rule.priority" [severity]="prioritySeverity(rule.priority)"></p-tag>
                    </div>
                    <div class="copy-rule-text">{{ rule.ruleText }}</div>
                  </div>
                </li>
              }
            </ul>
          </div>
          <p class="pdf-hint">
            <span>ⓘ</span>
            Copied rules will be created in <strong>{{ selectedRepo()?.projectName }}</strong> with the scope selected above. Repository-specific scope from the source project is not preserved.
          </p>
        }
      }

      <div class="form-actions">
        <p-button
          [label]="copySaving() ? 'Copying…' : (copySelectedRuleIds().size > 0 ? 'Copy selected' : 'Copy all')"
          [loading]="copySaving()"
          [disabled]="!copySourceProjectId() || copySourceRules().length === 0"
          (onClick)="confirmCopy()">
        </p-button>
        <p-button
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (onClick)="cancelCopyPanel()">
        </p-button>
      </div>
    </div>
  }

  @if (loading()) {
    <div class="empty-state">Loading…</div>
  } @else if (error()) {
    <p-message severity="error" [text]="error()!" styleClass="w-full"></p-message>
  } @else if (activeTab() === 'rules') {
    <p-table
      [value]="rules()"
      [rowHover]="true"
      [stripedRows]="true"
      styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Scope</th>
          <th>Category</th>
          <th>Priority</th>
          <th>Title</th>
          <th>Rule</th>
          <th>Active</th>
          <th></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-rule>
        <tr [class.inactive-row]="!rule.isActive">
          <td>
            <span class="scope-pill" [class.scope-repo]="rule.repositoryId">
              {{ rule.repositoryName ?? 'Project-wide' }}
            </span>
          </td>
          <td>{{ rule.categoryName }}</td>
          <td>
            <p-tag
              [value]="rule.priority"
              [severity]="prioritySeverity(rule.priority)">
            </p-tag>
          </td>
          <td class="title-cell">{{ rule.title }}</td>
          <td class="rule-text-cell">{{ rule.ruleText }}</td>
          <td>
            <span class="active-dot" [class.on]="rule.isActive"></span>
          </td>
          <td class="actions-cell">
            <p-button
              label="Edit"
              size="small"
              severity="secondary"
              [outlined]="true"
              (onClick)="openEditRule(rule)">
            </p-button>
            <p-button
              label="Delete"
              size="small"
              severity="danger"
              [outlined]="true"
              (onClick)="deleteRule(rule)">
            </p-button>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7" class="empty-table-msg">
            No rules yet. Click <strong>+ Add Rule</strong> to create the first one.
          </td>
        </tr>
      </ng-template>
    </p-table>
  } @else {
    <p-table
      [value]="brds()"
      [rowHover]="true"
      [stripedRows]="true"
      styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Scope</th>
          <th>Title</th>
          <th>Version</th>
          <th>Status</th>
          <th>Approved By</th>
          <th>Approved At</th>
          <th></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-brd>
        <tr>
          <td>
            <span class="scope-pill" [class.scope-repo]="brd.repositoryId">
              {{ brd.repositoryName ?? 'Project-wide' }}
            </span>
          </td>
          <td class="title-cell">{{ brd.title }}</td>
          <td>{{ brd.version }}</td>
          <td>
            <p-tag
              [value]="brdStatusLabel(brd.status)"
              [severity]="brdStatusSeverity(brd.status)">
            </p-tag>
          </td>
          <td>{{ brd.approvedBy ?? '—' }}</td>
          <td>{{ brd.approvedAt ? (brd.approvedAt | localDate:'short') : '—' }}</td>
          <td class="actions-cell">
            @if (approvingBrdId() === brd.id) {
              <input
                pInputText
                type="text"
                class="approve-input"
                [ngModel]="approveByInput()"
                (ngModelChange)="approveByInput.set($event)"
                placeholder="Your name" />
              <p-button
                label="Confirm"
                size="small"
                severity="success"
                (onClick)="confirmApprove(brd)">
              </p-button>
              <p-button
                label="Cancel"
                size="small"
                severity="secondary"
                [outlined]="true"
                (onClick)="cancelApprove()">
              </p-button>
            } @else {
              <p-button
                [label]="viewingBrdId() === brd.id ? 'Hide' : 'View'"
                size="small"
                [outlined]="true"
                (onClick)="toggleBrdView(brd)">
              </p-button>
              <p-button
                label="Edit"
                size="small"
                severity="secondary"
                [outlined]="true"
                (onClick)="openEditBrd(brd)">
              </p-button>
              @if (brd.status !== 'Approved') {
                <p-button
                  label="Approve"
                  size="small"
                  severity="success"
                  [outlined]="true"
                  (onClick)="startApprove(brd)">
                </p-button>
              }
              <p-button
                label="Delete"
                size="small"
                severity="danger"
                [outlined]="true"
                (onClick)="deleteBrd(brd)">
              </p-button>
            }
          </td>
        </tr>
        @if (viewingBrdId() === brd.id) {
          <tr class="brd-view-row">
            <td colspan="7">
              <div class="brd-view-pane">
                <app-markdown-preview [source]="brd.content"></app-markdown-preview>
              </div>
            </td>
          </tr>
        }
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7" class="empty-table-msg">
            No BRDs yet. Click <strong>+ Add BRD</strong> to create the first one.
          </td>
        </tr>
      </ng-template>
    </p-table>
  }
}
